import { Component, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}
interface State { hasError: boolean; error?: Error }

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: unknown) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }

  reset = () => this.setState({ hasError: false, error: undefined });

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="mx-auto my-8 max-w-xl rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center">
        <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-destructive" />
        <h2 className="text-lg font-semibold text-foreground">
          {this.props.fallbackTitle || "Something went wrong"}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          This section failed to load. The rest of the dashboard is still usable.
        </p>
        <div className="mt-4 flex justify-center gap-2">
          <Button size="sm" variant="outline" onClick={this.reset}>Try again</Button>
          <Button size="sm" variant="secondary" onClick={() => window.location.reload()}>Reload page</Button>
        </div>
      </div>
    );
  }
}
